/**
 * CampaignWaitForConnectPure
 *
 * Card-style replacement for the legacy `YesNoSwitchPure` branching UI on
 * `wait_for_connect` steps. Renders the decision as a labeled fork with
 * two branch rows, each previewing the immediate next step the cadence
 * would take. The "No" branch's wait length is inlined into the row and
 * editable in place (click → number input + Update button — no popup).
 *
 * Render contract:
 *   - `state === 'skipped'` → the contact is already connected, so the
 *     Yes branch is treated as taken and the No branch is hidden /
 *     dimmed.
 *   - `state === 'finished'` → both branches faded; useful for past
 *     cadence rendering.
 *   - default                → both branches shown, No branch wait pill
 *     editable.
 *
 * Branch previews are passed in by the host (see `BranchPreview`) so
 * this component stays pure and platform-agnostic. The host walks the
 * cadence graph to produce the preview labels.
 */
import type { ReactNode } from 'react';
import { useState } from 'react';

import type { CadenceStepStatePure, CadenceStepTypePure } from '../CGStep/types';
import { cn } from '../cn';
import Button from '../Input2/Button';
import Input from '../Input2/Input';
import ToggleSwitch from '../Input2/ToggleSwitch';
import StepIconSvg from '../StepIcon/StepIconSvg';
import { Editor, General } from '../StrokeIcons';

const DAY_MS = 24 * 60 * 60 * 1000;

function daysToMs(days: number): number {
  return days * DAY_MS;
}

function msToDays(ms: number): number {
  return Math.round(ms / DAY_MS);
}

export type WaitForConnectBranchPreview = {
  /**
   * Step type for the icon. Pass `undefined` if the branch terminates
   * (e.g. the cadence ends if the contact accepts immediately) and the
   * preview row will render an "End of cadence" placeholder instead.
   */
  type?: CadenceStepTypePure;
  /**
   * Human-readable description of what happens on this branch. Free
   * text so the host can include richer info (e.g. "Send message in 2
   * days", "Send email + 1 follow-up").
   */
  label: string;
};

export type CampaignWaitForConnectPureProps = {
  /** Wait length on the "No" branch, in milliseconds. */
  delay: number;
  /** Current step state. Drives dimming / hiding of branches. */
  state?: CadenceStepStatePure;
  /**
   * Preview of the IMMEDIATE next step on the Yes branch, used as a
   * subtitle on the Yes row. The actual Yes subtree renders OUTSIDE
   * the card (below it) so the rest of the cadence reads as the main
   * timeline — the host should add a "Yes path continues ↓" banner
   * between this card and those cards (or render
   * `<CampaignWaitForConnectYesBanner />` from this file).
   */
  yesPreview?: WaitForConnectBranchPreview;
  /**
   * Children rendered nested INSIDE the No branch row. Pass the host's
   * recursive render of `node.next.two` here so the No-path subtree
   * sits visually under the No branch (indented) — making it obvious
   * which steps are conditional on "not accepted". When the No branch
   * has no downstream step yet, pass an "add step" affordance so the
   * user can extend it from this row.
   */
  noBranchChildren?: ReactNode;
  /** Fired when the user submits a new wait length (in ms) for the No branch. */
  /**
   * Fired when the user edits the "not accepted after N days" wait.
   * When omitted the wait pill renders non-interactive (read-only hosts
   * like the extension sidepanel).
   */
  onChangeDelay?: (delayMs: number) => void;
  /**
   * Auto-withdraw state of the sibling `connect` step: when true (the
   * default), the still-pending LinkedIn invitation is withdrawn once
   * this No branch resolves. Omit (leave `undefined`) to hide the chip —
   * used when there's no sibling connect step to configure.
   */
  autoWithdraw?: boolean | null;
  /**
   * Fired when the user toggles auto-withdraw. The host writes this back
   * to the sibling connect step. When omitted (read-only hosts, or a
   * resolved/finished card) the chip renders as a static On/Off label
   * instead of an interactive toggle.
   */
  onChangeAutoWithdraw?: (value: boolean) => void;
  className?: string;
};

export function CampaignWaitForConnectPure({
  delay,
  state,
  yesPreview,
  noBranchChildren,
  onChangeDelay,
  autoWithdraw,
  onChangeAutoWithdraw,
  className,
}: CampaignWaitForConnectPureProps) {
  const [editing, setEditing] = useState(false);
  const days = msToDays(delay);
  const isAccepted = state === 'skipped';
  const isFinished = state === 'finished';
  // Show the chip only when the host supplies a connect-step context
  // (either the current value or a change handler). A finished card
  // renders it read-only — the outcome already fired, so the flag is no
  // longer editable.
  const showAutoWithdraw = autoWithdraw != null || onChangeAutoWithdraw != null;

  return (
    <div
      data-testid="campaign-wait-for-connect"
      data-state={state ?? 'not-started'}
      className={cn(
        'flex w-full flex-col overflow-hidden rounded-lg border shadow-xs',
        'border-lynch-200 bg-white dark:border-slate-800 dark:bg-slate-900',
        className,
      )}>
      {/* Header */}
      <div
        className="flex items-baseline justify-between gap-2 border-b border-lynch-200 px-3 py-2 dark:border-slate-800"
        data-testid="campaign-wait-for-connect-header">
        <span className="text-xs font-semibold uppercase tracking-wide text-lynch-700 dark:text-lynch-300">
          Did the contact accept the invitation?
        </span>
        {isFinished && (
          <span className="text-[10px] font-medium uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
            done
          </span>
        )}
      </div>

      {/* No branch first — its conditional subtree renders nested INSIDE
          the row so the user can scan which steps only fire when the
          contact does NOT accept in time. Comes before the Yes row so
          the special-case fork is read top-to-bottom: "if not accepted
          do this; otherwise continue with the main timeline below". */}
      {!isAccepted && (
        <div data-testid="campaign-wait-for-connect-no-wrapper">
          <BranchRow
            data-testid="campaign-wait-for-connect-no"
            tone="no"
            dimmed={isFinished}
            label={
              <span className="flex flex-col gap-1.5">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span>No — not accepted after</span>
                  {!editing && onChangeDelay != null && (
                    <button
                      type="button"
                      data-testid="campaign-wait-for-connect-wait-pill"
                      onClick={() => setEditing(true)}
                      className={cn(
                        'group inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors',
                        'border-lynch-300 bg-lynch-50 text-lynch-800 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700',
                        'dark:border-slate-600 dark:bg-slate-800 dark:text-lynch-200 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-300',
                      )}>
                      <span data-testid="campaign-wait-for-connect-days">
                        {days} {days === 1 ? 'day' : 'days'}
                      </span>
                      <Editor.Pencil03 className="size-3 opacity-0 transition-opacity group-hover:opacity-70" />
                    </button>
                  )}
                  {/* Read-only pill — same chrome, no click affordance, no
                    hover/pencil, so the wait still reads but doesn't
                    promise an edit the host can't deliver. */}
                  {!editing && onChangeDelay == null && (
                    <span
                      data-testid="campaign-wait-for-connect-wait-pill"
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                        'border-lynch-300 bg-lynch-50 text-lynch-800',
                        'dark:border-slate-600 dark:bg-slate-800 dark:text-lynch-200',
                      )}>
                      <span data-testid="campaign-wait-for-connect-days">
                        {days} {days === 1 ? 'day' : 'days'}
                      </span>
                    </span>
                  )}
                  {editing && (
                    <InlineDelayEdit
                      initialDays={days}
                      onCancel={() => setEditing(false)}
                      onSubmit={(newDays) => {
                        onChangeDelay?.(daysToMs(newDays));
                        setEditing(false);
                      }}
                    />
                  )}
                </span>
                {/* Auto-withdraw the pending invite when this No branch
                    resolves. Editable in place (mirrors the wait pill);
                    read-only once the card is finished or the host omits
                    the change handler. */}
                {showAutoWithdraw && (
                  <AutoWithdrawChip
                    checked={autoWithdraw ?? true}
                    onChange={isFinished ? undefined : onChangeAutoWithdraw}
                  />
                )}
              </span>
            }>
            {noBranchChildren != null && (
              <div
                data-testid="campaign-wait-for-connect-no-children"
                // Just a red left-border accent — no surrounding pink
                // box. Negative left margin pulls the border back under
                // the marker (✗) column so it lines up flush with the
                // header divider above. `pl-3` insets the actual
                // children (day card / picker / trigger) a touch off
                // the border so they don't kiss it.
                className="-ml-8 mt-2 w-[calc(100%+2rem)] self-stretch border-l-2 border-fair-pink-300 pl-2 dark:border-fair-pink-700">
                {noBranchChildren}
              </div>
            )}
          </BranchRow>
        </div>
      )}

      {/* Yes branch — preview only; the actual Yes subtree renders
          OUTSIDE this card so the cadence's main timeline isn't nested
          inside a fork row. The "continues below ↓" hint makes the
          relationship to the cards below explicit. */}
      <div
        data-testid="campaign-wait-for-connect-yes-wrapper"
        className={cn(!isAccepted && 'border-t border-lynch-200 dark:border-slate-800')}>
        <BranchRow
          data-testid="campaign-wait-for-connect-yes"
          tone="yes"
          active={isAccepted}
          dimmed={isFinished}
          label="Yes — accepted"
          preview={yesPreview}
          footer={
            <span
              data-testid="campaign-wait-for-connect-yes-continues"
              className="text-[11px] font-medium text-mantis-700 dark:text-mantis-300">
              ↓ continues below
            </span>
          }
        />
      </div>
    </div>
  );
}

/**
 * Banner the host renders BETWEEN the wait-for-connect card and the
 * cards that follow (Yes path subtree). Visually labels the downstream
 * cards as the "Yes" continuation so the conditional fork is obvious.
 */
export function CampaignWaitForConnectYesBanner({ className }: { className?: string }) {
  return (
    <div
      data-testid="campaign-wait-for-connect-yes-banner"
      className={cn(
        'flex w-full items-center gap-2 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-mantis-700 dark:text-mantis-300',
        className,
      )}>
      <span aria-hidden className="h-px flex-1 bg-mantis-300 dark:bg-mantis-700" />
      <span>If accepted</span>
      <span aria-hidden className="h-px flex-1 bg-mantis-300 dark:bg-mantis-700" />
    </div>
  );
}

type BranchTone = 'yes' | 'no';

function BranchRow({
  tone,
  active,
  dimmed,
  label,
  preview,
  footer,
  children,
  ...rest
}: {
  tone: BranchTone;
  active?: boolean;
  dimmed?: boolean;
  label: React.ReactNode;
  preview?: WaitForConnectBranchPreview;
  footer?: ReactNode;
  children?: ReactNode;
  'data-testid'?: string;
}) {
  const isYes = tone === 'yes';
  return (
    <div {...rest} className={cn('flex w-full items-start gap-3 px-3 py-2.5', dimmed && 'opacity-50')}>
      <span
        aria-hidden
        data-testid="campaign-wait-for-connect-branch-marker"
        className={cn(
          'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center',
          isYes
            ? active
              ? 'text-mantis-600 dark:text-mantis-400'
              : 'text-mantis-700 dark:text-mantis-300'
            : 'text-fair-pink-700 dark:text-fair-pink-300',
        )}>
        {isYes ? <General.CheckCircle className="size-5" /> : <General.XCircle className="size-5" />}
      </span>
      <div className="flex min-w-0 grow flex-col gap-1">
        <div className="flex items-center text-sm text-lynch-900 dark:text-lynch-100">{label}</div>
        {preview != null && <BranchPreviewRow preview={preview} />}
        {footer}
        {children}
      </div>
    </div>
  );
}

function BranchPreviewRow({ preview }: { preview?: WaitForConnectBranchPreview }) {
  if (preview == null) {
    return (
      <span
        data-testid="campaign-wait-for-connect-preview-end"
        className="inline-flex items-center gap-1.5 text-xs italic text-lynch-500 dark:text-lynch-400">
        End of cadence
      </span>
    );
  }
  return (
    <span
      data-testid="campaign-wait-for-connect-preview"
      className="inline-flex items-center gap-1.5 text-xs text-lynch-700 dark:text-lynch-300">
      {preview.type != null && (
        <StepIconSvg
          iconType={preview.type}
          className="size-3.5 text-lynch-500 dark:text-lynch-400"
          data-testid="campaign-wait-for-connect-preview-icon"
        />
      )}
      <span className="truncate">{preview.label}</span>
    </span>
  );
}

function InlineDelayEdit({
  initialDays,
  onCancel,
  onSubmit,
}: {
  initialDays: number;
  onCancel: () => void;
  onSubmit: (days: number) => void;
}) {
  const [days, setDays] = useState(initialDays);
  return (
    <form
      data-testid="campaign-wait-for-connect-edit"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(days);
      }}
      className="inline-flex items-center gap-1">
      <Input
        type="number"
        min={0}
        value={days}
        onChange={(e) => setDays(Number(e.target.value))}
        autoFocus
        size="sm"
        className="w-16"
      />
      <Button type="submit" size="sm" color="blue">
        Update
      </Button>
      <Button type="button" size="sm" color="gray" text onClick={onCancel} aria-label="Cancel">
        Cancel
      </Button>
    </form>
  );
}

/**
 * Compact chip on the No branch that surfaces — and, when editable,
 * toggles — the sibling connect step's auto-withdraw flag. Shares the
 * wait-pill chrome so the two No-branch controls read as a set.
 *
 * `onChange` omitted → static "On/Off" label (read-only hosts, or a
 * finished card whose outcome already fired). `onChange` provided →
 * interactive `ToggleSwitch`.
 */
function AutoWithdrawChip({ checked, onChange }: { checked: boolean; onChange?: (value: boolean) => void }) {
  const readOnly = onChange == null;
  return (
    <span
      data-testid="campaign-wait-for-connect-autowithdraw"
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
        'border-lynch-300 bg-lynch-50 text-lynch-800',
        'dark:border-slate-600 dark:bg-slate-800 dark:text-lynch-200',
      )}>
      <span>Auto-withdraw invite</span>
      {readOnly && (
        <span data-testid="campaign-wait-for-connect-autowithdraw-state" className="text-lynch-600 dark:text-lynch-400">
          {checked ? 'On' : 'Off'}
        </span>
      )}
      {!readOnly && (
        <ToggleSwitch
          data-testid="campaign-wait-for-connect-autowithdraw-toggle"
          size="xs"
          color="blue"
          checked={checked}
          onChange={onChange}
        />
      )}
    </span>
  );
}
